import { z } from 'zod';

/**
 * W4c DUTY WATCH (coordination council A.3 / #526, migration 0130) — the shared shape for a
 * per-DESK "Watching / On watch" flag on a file surface (incident / assistance request /
 * agency task). A desk marks "eyes on this file" WITHOUT owning it. NON-EXCLUSIVE + additive:
 * N desks watch one file at once; a watch never evicts another and is never a claim.
 *
 * Watching is NOT command (App A #11), NOT ownership (the signed §4.3 Incident-Command
 * handle), NOT the Situation Unit monitoring function (App A #76). DESCRIPTIVE-NO-COLLISION.
 */

/** The watchable file surfaces (lockstep with valid_watch_source_types, migration 0130). */
export const WATCH_SOURCE_TYPES = ['incident', 'assistance_request', 'agency_task'] as const;
export const WatchSourceTypeSchema = z.enum(WATCH_SOURCE_TYPES);
export type WatchSourceType = z.infer<typeof WatchSourceTypeSchema>;

/**
 * The watch state of a file for the CURRENT desk. `watching` = the current operator's OWN
 * desk (agency) has an active watch; `count` = the number of desks with an active watch on
 * this file.
 *
 * ⚠ `count` is the CROSS-TENANT aggregate — DORMANT until #190 (council A.8). Under
 * single-agency RLS a scoped read sees only the operator's own desk (0/1); the genuine
 * multi-tenant "N desks watching" needs a #190 broker read. In the pre-RBAC demo only one
 * operator seat exists, so this reads 0/1 (a NAMED SCOPE-OUT, not a defect).
 */
export interface DeskWatchState {
  source_type: WatchSourceType;
  source_id: string;
  /** The current desk (agency) is actively watching this file. */
  watching: boolean;
  /** Active desks watching this file (cross-tenant aggregate — dormant until #190). */
  count: number;
}
